import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { projectService } from '@/services/projectService';
import { userService } from '@/services/userService';
import { useProjectStore } from '@/store/projectStore';
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { Project } from '@/types/project';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Modal } from '@/components/common/Modal';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ProjectCard } from '@/components/projects/ProjectCard';
import { ProjectForm } from '@/components/projects/ProjectForm';
import { ProjectMembers } from '@/components/projects/ProjectMembers';
import { formatAPIError } from '@/utils/errorHandlers';

type ViewMode = 'grid' | 'list';

export function ProjectsPage() {
  const queryClient = useQueryClient();
  const { currentProject, setCurrentProject } = useProjectStore();
  const { user } = useAuthStore();
  const addNotification = useNotificationStore((state) => state.addNotification);

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [defaultProjectId, setDefaultProjectId] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      if (stored) {
        const user = JSON.parse(stored);
        return user?.preferences?.default_project_id || null;
      }
    } catch {}
    return null;
  });
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Fetch projects
  const { data: projects = [], isLoading, error } = useQuery({
    queryKey: ['projects', user?.id],
    queryFn: () => projectService.getProjects(),
  });

  // Delete project mutation
  const deleteMutation = useMutation({
    mutationFn: (projectId: string) => projectService.deleteProject(projectId),
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Project Deleted',
        message: 'The project has been deleted successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['projects'] });

      // Clear current project if it was deleted
      if (currentProject?.id === selectedProject?.id) {
        setCurrentProject(null);
      }

      setIsDeleteModalOpen(false);
      setSelectedProject(null);
    },
    onError: (error: any) => {
      const message = formatAPIError(error, 'Failed to delete project');
      addNotification({
        type: 'error',
        title: 'Error',
        message,
      });
    },
  });

  // Set default project mutation
  const setDefaultMutation = useMutation({
    mutationFn: (projectId: string) =>
      userService.updateMe({ preferences: { default_project_id: projectId } }),
    onSuccess: (updatedUser: any, projectId: string) => {
      setDefaultProjectId(projectId);
      // Persist to localStorage
      try {
        const stored = localStorage.getItem('auth_user');
        if (stored) {
          const user = JSON.parse(stored);
          user.preferences = { ...(user.preferences || {}), default_project_id: projectId };
          localStorage.setItem('auth_user', JSON.stringify(user));
        }
      } catch {}
      addNotification({ type: 'success', title: 'Default project set', message: 'You will land here on next login.' });
    },
    onError: () => {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to set default project.' });
    },
  });

  // Filter projects based on search query
  const filteredProjects = projects.filter((project) => {
    const query = searchQuery.toLowerCase();
    return (
      project.name.toLowerCase().includes(query) ||
      project.key.toLowerCase().includes(query) ||
      project.customer_name?.toLowerCase().includes(query) ||
      project.description?.toLowerCase().includes(query)
    );
  });

  const handleSelectProject = (project: Project) => {
    setCurrentProject(project);
    addNotification({
      type: 'success',
      title: 'Project Selected',
      message: `You are now working on ${project.name}`,
    });
  };

  const handleEditClick = (project: Project) => {
    setSelectedProject(project);
    setIsEditModalOpen(true);
  };

  const handleMembersClick = (project: Project) => {
    setSelectedProject(project);
    setIsMembersModalOpen(true);
  };

  const handleDeleteClick = (project: Project) => {
    setSelectedProject(project);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedProject) {
      deleteMutation.mutate(selectedProject.id);
    }
  };

  const handleCreateSuccess = (project: Project) => {
    setIsCreateModalOpen(false);
    // Auto-select the newly created project
    setCurrentProject(project);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setSelectedProject(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-danger-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Failed to load projects
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            There was an error loading your projects. Please try again.
          </p>
          <Button
            variant="primary"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['projects'] })}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Projects</h1>
        <p className="text-gray-600">
          Manage your projects and select the one you want to work on
        </p>
      </div>

      {/* Actions Bar */}
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1 w-full sm:max-w-md">
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
          />
        </div>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Grid view"
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="List view"
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>

          <Button
            variant="primary"
            leftIcon={<PlusIcon className="h-5 w-5" />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            New Project
          </Button>
        </div>
      </div>

      {/* Projects List/Grid */}
      {filteredProjects.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <EmptyState
            title={searchQuery ? 'No projects found' : 'No projects yet'}
            description={
              searchQuery
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first project'
            }
            action={
              !searchQuery && (
                <Button
                  variant="primary"
                  leftIcon={<PlusIcon className="h-5 w-5" />}
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  Create Project
                </Button>
              )
            }
          />
        </div>
      ) : (
        <div
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
              : 'space-y-3'
          }
        >
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isSelected={currentProject?.id === project.id}
              isDefault={defaultProjectId === project.id}
              onSelect={handleSelectProject}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              onManageMembers={handleMembersClick}
              onSetDefault={(p) => setDefaultMutation.mutate(p.id)}
            />
          ))}
        </div>
      )}

      {/* Projects Count */}
      {filteredProjects.length > 0 && (
        <div className="mt-6 text-sm text-gray-600 text-center">
          Showing {filteredProjects.length} of {projects.length} project
          {projects.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Create Project Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Project"
        size="lg"
      >
        <ProjectForm
          onSuccess={handleCreateSuccess}
          onCancel={() => setIsCreateModalOpen(false)}
        />
      </Modal>

      {/* Edit Project Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProject(null);
        }}
        title="Edit Project"
        size="lg"
      >
        {selectedProject && (
          <ProjectForm
            project={selectedProject}
            onSuccess={handleEditSuccess}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedProject(null);
            }}
          />
        )}
      </Modal>

      {/* Members Modal */}
      <Modal
        isOpen={isMembersModalOpen}
        onClose={() => {
          setIsMembersModalOpen(false);
          setSelectedProject(null);
        }}
        title="Project Members"
        size="lg"
      >
        {selectedProject && (
          <ProjectMembers
            project={selectedProject}
            onClose={() => {
              setIsMembersModalOpen(false);
              setSelectedProject(null);
            }}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedProject(null);
        }}
        title="Delete Project"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-danger-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-900 font-medium mb-2">
                Are you sure you want to delete this project?
              </p>
              {selectedProject && (
                <div className="bg-gray-50 rounded p-3 mb-3">
                  <p className="text-sm font-semibold text-gray-900">
                    {selectedProject.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Key: {selectedProject.key}
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-600">
                This action cannot be undone. All tickets and data associated with
                this project will be preserved but the project will be marked as inactive.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedProject(null);
              }}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              isLoading={deleteMutation.isPending}
            >
              Delete Project
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ProjectsPage;
